export type FilterDefinition = {
	component: string
	field: string
	name: string
	icon: string
	type?: 'autocomplete' | 'date' | 'date-from' | 'date-to'
	value: {
		field: string
		display: string | string[]
		group?: string
	}
	text?: {
		title: {
			layout: string
			items: string[]
		}
		subtitle?: string
	}
}

const accounts: FilterDefinition = {
	component: 'Accounts',
	field: 'account_id',
	icon: 'fa-fw fal fa-wallet',
	name: 'component.accounts.name',
	type: 'autocomplete',
	value: {
		field: 'id',
		display: ['account_name.name'],
	},
}

const accountingYears: FilterDefinition = {
	component: 'AccountingYears',
	field: 'accounting_year_id',
	icon: 'fas fa-calendar-range',
	name: 'component.accounts.accountingYear',
	type: 'autocomplete',
	value: {
		field: 'id',
		display: 'year',
	},
}

const buildings: FilterDefinition = {
	component: 'Buildings',
	field: 'buildings_id',
	icon: 'fa-fw fa-building',
	name: 'component.management.settings.objects',
	text: {
		subtitle: undefined,
		title: { layout: '{0} - {1}', items: ['hp_id', 'address.street'] },
	},
	type: 'autocomplete',
	value: {
		field: 'id',
		display: ['address.street', 'address.zip', 'address.city'],
	},
}
const buildingsNotes: FilterDefinition = {
	...buildings,
	...{ field: 'notes_buildings.buildings_id' },
}
const buildingsMeter: FilterDefinition = {
	...buildings,
	...{ field: 'meter.building_id' },
}

const craftmanTaskProcess: FilterDefinition = {
	component: 'CraftsmanTaskProcess',
	field: 'process_id',
	icon: 'fal fa-cabinet-filing',
	name: 'component.craftsmanTasks.process',
	value: {
		field: 'id',
		display: 'name',
	},
}
const craftmanTaskStatus: FilterDefinition = {
	component: 'CraftsmanTaskStatus',
	field: 'status_id',
	icon: 'fal fa-tasks-alt',
	name: 'Status',
	value: {
		field: 'id',
		display: 'name',
	},
}
const craftmanTaskType: FilterDefinition = {
	component: 'CraftsmanTaskType',
	field: 'type_id',
	icon: 'fal fa-tags',
	name: 'component.craftsmanTasks.subject_type',
	value: {
		field: 'id',
		display: 'name',
	},
}

const cistype: FilterDefinition = {
	component: 'CisType',
	field: 'cis_type_id',
	icon: 'fas fa-comment-lines',
	name: 'entity.cis.fields.cisType',
	value: {
		field: 'id',
		display: 'description',
	},
}
const documenttype: FilterDefinition = {
	component: 'DocumentTypes',
	field: 'file_type',
	icon: 'fa-fw far fa-book',
	name: 'common.type',
	type: 'autocomplete',
	value: {
		field: 'id',
		display: 'display_label',
	},
}
const date: FilterDefinition = {
	component: 'DatePicker',
	field: 'ts',
	icon: 'fa-fw far fa-calendar-day',
	name: 'common.date',
	type: 'date',
	value: {
		field: 'ts',
		display: 'formatted',
	},
}
const metertypes: FilterDefinition = {
	component: 'MeterType',
	field: 'device_type_id',
	icon: 'fas fa-tachometer-alt',
	name: 'Meter',
	text: {
		subtitle: undefined,
		title: { layout: '{0}', items: ['meter_id'] },
	},
	value: {
		field: 'id',
		display: 'description',
	},
}
const notestype: FilterDefinition = {
	component: 'NotesType',
	field: 'notes_type_id',
	icon: 'fa-fw fal fa-sticky-note',
	name: 'entity.notestype.name',
	value: {
		field: 'id',
		display: 'name',
	},
}
const rolepermissions: FilterDefinition = {
	component: 'Roles',
	field: 'role_permissions.role_id',
	icon: 'fa-fw far fa-book',
	name: 'common.group',
	type: 'autocomplete',
	value: {
		field: 'id',
		group: 'id',
		display: 'name',
	},
}
const users: FilterDefinition = {
	component: 'Users',
	field: 'notifiable_id',
	icon: 'fad fa-users',
	name: 'entity.user.name',
	type: 'autocomplete',
	value: {
		field: 'user_id',
		display: ['salutation', 'firstname', 'lastname'],
	},
}

const role: FilterDefinition = {
	...rolepermissions,
	...{ field: 'role_permissions.role_id' },
}

const teams: FilterDefinition = {
	component: 'Teams',
	//zu filternder Wert in der collection -> Datentabelle
	field: 'team_id',
	icon: 'fal fa-chalkboard-teacher',
	name: 'component.teams.teamMember',
	type: 'autocomplete',
	value: {
		//Wert der zum filtern aus der Autocomplete Komponente benutzt wird
		field: 'id',
		display: ['salutation', 'first_name', 'last_name'],
	},
}

const propertymanagers: FilterDefinition = {
	component: 'PropertyManagers',
	field: 'propertymanager_id',
	icon: 'fa fa-fw fas fa-users',
	name: 'component.subscriptions.property_manager',
	type: 'autocomplete',
	value: {
		field: 'id',
		display: ['person.salutation', 'person.firstname', 'person.lastname'],
	},
}

const tasktype: FilterDefinition = {
	component: 'TaskType',
	field: 'task_type_id',
	icon: 'fa-fw fa-solid fa-clipboard-list-check',
	name: 'entity.task.name',
	value: {
		field: 'id',
		display: 'name',
	},
}

const reportStatus: FilterDefinition = {
	component: 'ReportStatus',
	field: 'report_status_id',
	icon: 'fal fa-tasks-alt',
	name: 'entity.reportStatus.name',
	value: {
		field: 'id',
		display: 'name',
	},
}

// Oberkategorie, nicht Untertyp: die Untertyp-Namen sind über die Kategorien hinweg
// mehrfach vergeben ("Schadensmeldung" 6x) und in einer flachen Liste ununterscheidbar.
const reportType: FilterDefinition = {
	component: 'ReportTypes',
	field: 'report_sub_type.report_type_id',
	icon: 'fal fa-tags',
	name: 'entity.reportType.name',
	value: {
		field: 'id',
		display: 'name',
	},
}

// Sachbearbeiter der Meldung — gleiche Teams-Auswahl, anderes Feld als `teams`.
const reportAssignedTeam: FilterDefinition = {
	...teams,
	...{ field: 'assigned_team_id', name: 'entity.team.fields.user' },
}

// Zeitraum-Grenzen. Eigene `component`-Werte, weil der Filter-Store pro Tabelle
// über `definition.component` dedupliziert — zwei mal 'DatePicker' würde kollidieren.
const dateFrom: FilterDefinition = {
	component: 'DatePickerFrom',
	field: 'ts',
	icon: 'fa-fw far fa-calendar-day',
	name: 'common.dateFrom',
	type: 'date-from',
	value: {
		field: 'ts',
		display: 'formatted',
	},
}
const dateTo: FilterDefinition = {
	...dateFrom,
	...{ component: 'DatePickerTo', name: 'common.dateTo', type: 'date-to' },
}

export const filters = {
	accountingYears,
	accounts,
	buildings,
	buildingsMeter,
	buildingsNotes,
	cistype,
	craftmanTaskProcess,
	craftmanTaskStatus,
	craftmanTaskType,
	date,
	documenttype,
	metertypes,
	notestype,
	dateFrom,
	dateTo,
	propertymanagers,
	reportAssignedTeam,
	reportStatus,
	reportType,
	role,
	rolepermissions,
	tasktype,
	teams,
	users,
}

export default filters

export type FilterVariant = keyof typeof filters
